import React, { useContext, useRef, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import clsx from 'clsx';

// components
import CardSlot from './CardSlot';
import EmptyCardSlot from './EmptyCardSlot';
import StickyMobileHeader from './StickyMobileHeader';
import {
  CompareCarouselControls,
  Typography,
  MatchHeightProvider
} from '@americanexpress/aqx-components';

// selectors
import { getHeader, getSubHeader, getCompareSliderDTM } from '../redux';

// enums
import { CurrentPermutationContext } from '../index';

// styles
import styles from '../__styles__/mobileHeader.scss?modules';

// utils
import { sendDTMClickEvent } from 'service_modules/models/dtm-tagging';

const useOnScreen = ref => {
  const [isBelow, setIsBelow] = useState(false);
  const observerRef = useRef(null);

  const callback = ([entry]) => {
    if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
      setIsBelow(true);
    } else {
      setIsBelow(false);
    }
  };

  useEffect(() => {
    observerRef.current = new IntersectionObserver(callback);
  }, []);

  useEffect(
    () => {
      observerRef.current.observe(ref.current);

      return () => {
        observerRef.current.disconnect();
      };
    },
    [ref]
  );

  return isBelow;
};

const Mobile = ({ header, description, compareSliderDTM, selectedCards }) => {
  const {
    goToNextPermutation,
    goToPrevPermutation,
    goToSpecificPermutation,
    currentPermutations,
    prevPermutation,
    nextPermutation,
    currentPermutationIndex
  } = useContext(CurrentPermutationContext);
  const ref = useRef(null);
  const belowCarouselControls = useOnScreen(ref);
  const { leftArrowDTM, rightArrowDTM, dotDTM } = compareSliderDTM;

  return (
    <div
      className={clsx(styles.container, {
        [styles.addMargin]: belowCarouselControls
      })}
    >
      <Typography Component="h1" className={styles.header}>
        {header}
      </Typography>
      <Typography Component="p" className={styles.subHeader}>
        {description}
      </Typography>
      <MatchHeightProvider>
        <div className={styles.cardSlotContainer}>
          {selectedCards.map(
            (card, i) =>
              card ? (
                <CardSlot smallCardArt={false} card={card} key={i} />
              ) : (
                <EmptyCardSlot key={i} />
              )
          )}
        </div>
      </MatchHeightProvider>
      <div className={styles.carouselControls} ref={ref}>
        <CompareCarouselControls
          onPrevClick={() => {
            goToPrevPermutation();
            sendDTMClickEvent(leftArrowDTM);
          }}
          onNextClick={() => {
            goToNextPermutation();
            sendDTMClickEvent(rightArrowDTM);
          }}
          onDotClick={idx => {
            goToSpecificPermutation(idx);
            sendDTMClickEvent({
              ...dotDTM,
              eventAsset: `${dotDTM.eventAsset}${idx}`
            });
          }}
          prevClickAriaLabel={`Show Cards ${prevPermutation[0]} & ${
            prevPermutation[1]
          }`}
          nextClickAriaLabel={`Show Cards ${nextPermutation[0]} & ${
            nextPermutation[1]
          }`}
          firstDotAriaLabel="Page 1 of 3"
          secondDotAriaLabel="Page 2 of 3"
          thirdDotAriaLabel="Page 3 of 3"
          text={`Cards ${currentPermutations[0]} & ${currentPermutations[1]}`}
          activeIndex={currentPermutationIndex}
        />
      </div>
      <StickyMobileHeader
        shouldRender={belowCarouselControls}
        selectedCards={selectedCards}
      />
    </div>
  );
};

const mapStateToProps = state => ({
  header: getHeader(state),
  description: getSubHeader(state),
  compareSliderDTM: getCompareSliderDTM(state)
});

export default connect(mapStateToProps)(Mobile);
